import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Snowflake, Sparkles, Check, ArrowRight, Loader2, ArrowLeft,
  TrendingDown, AlertTriangle, ShieldCheck, DollarSign, Clock,
} from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  {
    key: 'monthly_spend',
    title: "What's your approximate monthly Snowflake spend?",
    sub: "Best estimate is fine — we use ranges.",
    options: [
      { value: 'lt_5k',    label: 'Less than $5K' },
      { value: '5_25k',    label: '$5K – $25K' },
      { value: '25_100k',  label: '$25K – $100K' },
      { value: '100k_plus',label: '$100K or more' },
    ],
  },
  {
    key: 'warehouse_count',
    title: 'How many warehouses are running in your account?',
    sub: 'Including dev/staging warehouses.',
    options: [
      { value: '1-5',   label: '1 – 5' },
      { value: '6-15',  label: '6 – 15' },
      { value: '16-50', label: '16 – 50' },
      { value: '50+',   label: '50+' },
    ],
  },
  {
    key: 'always_on_warehouses',
    title: 'How many warehouses run 24/7 without auto-suspend?',
    sub: 'This is the #1 source of Snowflake waste.',
    options: [
      { value: 'none',   label: 'None — everything auto-suspends' },
      { value: '1-2',    label: '1 – 2' },
      { value: '3-5',    label: '3 – 5' },
      { value: 'unsure', label: "I'm honestly not sure" },
    ],
  },
  {
    key: 'auto_suspend_timeout',
    title: 'What is your default auto-suspend timeout?',
    sub: 'Snowflake bills per-second after a 60s warm-up — long timeouts = waste.',
    options: [
      { value: '60s_or_less', label: '60 seconds or less' },
      { value: '5_min',       label: '5 minutes' },
      { value: '10_min',      label: '10 minutes' },
      { value: '15_plus_min', label: '15+ minutes' },
      { value: 'none',        label: 'Auto-suspend is disabled' },
    ],
  },
  {
    key: 'largest_warehouse_size',
    title: 'What is the size of your LARGEST warehouse?',
    sub: 'Each tier-up doubles credit consumption.',
    options: [
      { value: 'xs',      label: 'X-Small' },
      { value: 's',       label: 'Small' },
      { value: 'm',       label: 'Medium' },
      { value: 'l',       label: 'Large' },
      { value: 'xl_plus', label: 'X-Large or larger' },
    ],
  },
  {
    key: 'short_query_pct',
    title: 'What % of your queries finish in less than 10 seconds?',
    sub: 'High % + Large warehouse = over-provisioning.',
    options: [
      { value: 'lt_25',   label: 'Less than 25%' },
      { value: '25_50',   label: '25 – 50%' },
      { value: '50_75',   label: '50 – 75%' },
      { value: '75_plus', label: '75% or more' },
    ],
  },
  {
    key: 'multi_cluster',
    title: 'Are your warehouses configured as multi-cluster?',
    sub: 'Multi-cluster is great for concurrency spikes — wasteful when always-on.',
    options: [
      { value: 'always', label: 'Yes, always-on multi-cluster' },
      { value: 'some',   label: 'Some — auto-scale enabled' },
      { value: 'no',     label: 'No, single-cluster only' },
      { value: 'unsure', label: "Not sure" },
    ],
  },
  {
    key: 'storage_size',
    title: 'How much data are you storing in Snowflake?',
    sub: 'Total across all databases.',
    options: [
      { value: 'lt_1tb',     label: 'Less than 1 TB' },
      { value: '1_10tb',     label: '1 – 10 TB' },
      { value: '10_50tb',    label: '10 – 50 TB' },
      { value: '50tb_plus',  label: '50 TB or more' },
    ],
  },
  {
    key: 'time_travel_retention',
    title: 'What is your typical time-travel retention?',
    sub: 'Each extra day proportionally multiplies storage cost.',
    options: [
      { value: '1_day',   label: '1 day (default)' },
      { value: '7_days',  label: '7 days' },
      { value: '30_days', label: '30 days' },
      { value: '90_days', label: '90 days (Enterprise max)' },
    ],
  },
  {
    key: 'review_cadence',
    title: 'How often does your team review Snowflake costs?',
    sub: 'What you don\'t measure, you can\'t improve.',
    options: [
      { value: 'weekly',    label: 'Weekly' },
      { value: 'monthly',   label: 'Monthly' },
      { value: 'quarterly', label: 'Quarterly' },
      { value: 'rarely',    label: 'Rarely / never' },
    ],
  },
];

const gradeColor = (g) => ({
  A: { ring: 'ring-emerald-500/30', text: 'text-emerald-500',  bg: 'bg-emerald-500/10', label: 'Best-in-class' },
  B: { ring: 'ring-emerald-500/20', text: 'text-emerald-400',  bg: 'bg-emerald-500/10', label: 'Solid' },
  C: { ring: 'ring-amber-500/30',   text: 'text-amber-500',    bg: 'bg-amber-500/10',   label: 'Average' },
  D: { ring: 'ring-orange-500/30',  text: 'text-orange-500',   bg: 'bg-orange-500/10',  label: 'At-risk' },
  F: { ring: 'ring-rose-500/30',    text: 'text-rose-500',     bg: 'bg-rose-500/10',    label: 'Critical' },
}[g] || { ring: 'ring-slate-500/30', text: 'text-slate-400', bg: 'bg-slate-500/10', label: '' });

const SnowflakeHealthScore = () => {
  const [step, setStep] = useState(0); // 0..QUESTIONS.length-1, then "result"
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [insights, setInsights] = useState({ assessments_bucket: 'Just launched' });

  useEffect(() => {
    axios.get(`${API}/api/health-score/snowflake/insights`).then((r) => setInsights(r.data)).catch(() => {});
  }, []);

  const progressPct = step >= QUESTIONS.length ? 100 : Math.round((step / QUESTIONS.length) * 100);

  const onPick = async (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Last answer — submit
      setSubmitting(true);
      try {
        const { data } = await axios.post(`${API}/api/health-score/snowflake`, next);
        setResult(data);
        setStep(QUESTIONS.length); // result
      } catch (e) {
        toast.error('Couldn\'t calculate your score. Please try again.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const onBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const restart = () => {
    setAnswers({});
    setResult(null);
    setStep(0);
  };

  // ---------- RESULT VIEW ----------
  if (step === QUESTIONS.length && result) {
    const gc = gradeColor(result.grade);
    return (
      <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden">
        {/* Same atmospheric brand glow as the quiz view */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 left-1/3 w-[560px] h-[560px] rounded-full bg-cyan-500/[0.08] blur-[140px]" />
          <div className="absolute top-72 -right-20 w-[480px] h-[480px] rounded-full bg-emerald-500/[0.07] blur-[120px]" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
        </div>

        <header className="px-4 py-5 border-b border-white/5 relative z-10">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="hs-logo">
              <img src="/icons/cartie-emblem.png" alt="" className="w-7 h-7" />
              <span className="font-bold">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
            </Link>
            <button onClick={restart} className="text-sm text-slate-400 hover:text-white" data-testid="hs-restart-btn">
              Take it again
            </button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-12 sm:py-16 relative z-10">
          <div className="text-center mb-10">
            {/* Brand-mark above score — small real Snowflake logo */}
            <img
              src="https://api.iconify.design/logos:snowflake-icon.svg"
              alt="Snowflake"
              width="28"
              height="28"
              loading="lazy"
              className="w-7 h-7 mx-auto mb-3 opacity-80"
            />
            <div className={`inline-flex w-32 h-32 rounded-full items-center justify-center ring-8 ${gc.ring} ${gc.bg} mb-4 shadow-[0_8px_60px_-12px_rgba(34,211,238,0.4)]`}>
              <span className={`text-6xl font-extrabold ${gc.text}`} data-testid="hs-score">{result.score}</span>
            </div>
            <p className={`text-sm font-bold tracking-widest uppercase ${gc.text}`}>
              GRADE {result.grade} · {gc.label}
            </p>
            <h1 className="text-3xl sm:text-4xl font-extrabold mt-3 mb-2 tracking-tight">
              {result.verdict}
            </h1>
            <p className="text-slate-400">Based on your answers across 10 cost dimensions.</p>
          </div>

          {/* Waste estimate */}
          <div className="rounded-2xl bg-gradient-to-br from-rose-500/10 via-orange-500/5 to-transparent border border-rose-500/20 p-6 mb-8" data-testid="hs-waste-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-6 h-6 text-rose-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-rose-400 font-bold mb-1">Estimated waste</p>
                <p className="text-3xl sm:text-4xl font-extrabold mb-1" data-testid="hs-monthly-waste">
                  ${result.estimated_monthly_waste_usd.toLocaleString()}
                  <span className="text-base text-slate-400 font-medium ml-2">/ month</span>
                </p>
                <p className="text-sm text-slate-400">
                  ≈ <span className="text-orange-400 font-semibold">${result.estimated_annual_waste_usd.toLocaleString()}/year</span> · based on {result.monthly_spend_label} spend × {result.waste_pct}% waste signals
                </p>
              </div>
            </div>
          </div>

          {/* Top recommendations */}
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            Your top {result.top_recommendations.length} fix{result.top_recommendations.length > 1 ? 'es' : ''}
          </h2>
          <div className="space-y-3 mb-10">
            {result.top_recommendations.map((rec, i) => (
              <div
                key={i}
                className="rounded-xl bg-white/[0.03] border border-white/10 p-5 hover:bg-white/[0.05] transition-colors"
                data-testid={`hs-rec-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold mb-1">{rec.title}</p>
                    <p className="text-sm text-slate-400 leading-relaxed">{rec.fix}</p>
                    {rec.savings_pct_estimate > 0 && (
                      <span className="inline-flex items-center gap-1 mt-3 px-2 py-1 rounded-md text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        <TrendingDown className="w-3 h-3" />
                        Up to {rec.savings_pct_estimate}% saved
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Next CTA — newsletter + product */}
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            <NewsletterSignup
              source="snowflake_health_score_result"
              eyebrow="GET WEEKLY TIPS"
              title="Want more wins like these?"
              subtitle="The FinOps Brief — 3 cost-saving tips every Tuesday. Free."
            />
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-teal-500/5 border border-emerald-500/30 p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <p className="text-[11px] font-bold tracking-[0.2em] mb-3 text-emerald-400">DEEP-DIVE AUDIT</p>
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Run a real audit on your account.</h3>
                <p className="text-sm text-slate-400 mb-5">
                  Connect a read-only Snowflake role. Get the actual queries, warehouses, and storage burning your money — with one-click fixes.
                </p>
              </div>
              <Link to="/cloud-audit?provider=snowflake" data-testid="hs-cta-real-audit">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  Run real Snowflake audit
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Trust strip — uses bucketed assessments count (Iter84ag privacy) */}
          <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4 text-center">
            <p className="text-xs text-slate-400">
              {insights.assessments_bucket && insights.assessments_bucket !== 'Just launched'
                ? <>Joined <span className="text-emerald-400 font-semibold">{insights.assessments_bucket}</span> teams who've taken this assessment</>
                : 'Anonymous · No credentials shared · Used to benchmark your team'}
            </p>
          </div>
        </main>

        <footer className="border-t border-white/5 px-4 py-8 mt-10 text-center text-xs text-slate-400">
          &copy; 2026 CARTIEAI · Built by founders, for founders ·
          <Link to="/about" className="text-emerald-400 ml-1">Our Story</Link>
        </footer>
      </div>
    );
  }

  // ---------- LOADING ----------
  if (submitting) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-lg">Calculating your score…</p>
          <p className="text-sm text-slate-400 mt-2">This takes about 2 seconds.</p>
        </div>
      </div>
    );
  }

  // ---------- INTRO + QUIZ ----------
  const q = QUESTIONS[step];

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden">
      {/* Ambient brand atmosphere — Snowflake cyan + CARTIE emerald twin glows.
          Tasteful, no harsh gradients. Sits behind all content via -z-10. */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/3 w-[560px] h-[560px] rounded-full bg-cyan-500/[0.08] blur-[140px]" />
        <div className="absolute top-72 -right-20 w-[480px] h-[480px] rounded-full bg-emerald-500/[0.07] blur-[120px]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
      </div>

      <SEOHead
        title="Free Snowflake Cost Health Score — Get Your Score in 60s | CARTIEAI"
        description="Answer 7 questions, get your Snowflake Cost Health Score instantly. Find out how much you're overspending on Snowflake — free, no signup, 60 seconds."
        canonical="https://cartieai.com/snowflake-health-score"
        keywords="Snowflake cost optimization, Snowflake health score, Snowflake cost calculator, Snowflake waste"
        schema={{
          "@context": "https://schema.org",
          "@type": "Quiz",
          "name": "Snowflake Cost Health Score",
          "description": "7-question assessment of your Snowflake cost posture",
          "about": "Snowflake cost optimization",
          "provider": {
            "@type": "Organization",
            "name": "CARTIEAI",
            "url": "https://cartieai.com"
          }
        }}
      />
      <header className="px-4 py-5 border-b border-white/5 relative z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="hs-logo-quiz">
            <img src="/icons/cartie-emblem.png" alt="" className="w-7 h-7" />
            <span className="font-bold">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-white">Back to home</Link>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 mt-8 relative z-10">
        <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
          <span>Question {step + 1} of {QUESTIONS.length}</span>
          <span className="tabular-nums text-cyan-300/80 font-mono">{progressPct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full bg-cyan-400 bg-gradient-to-r from-cyan-400 via-emerald-400 to-emerald-500 transition-all duration-500 shadow-[0_0_12px_rgba(34,211,238,0.5)]"
            style={{ width: `${progressPct}%` }}
            data-testid="hs-progress-bar"
          />
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-10 sm:py-14 relative z-10">
        {step === 0 && (
          <div className="mb-10 text-center">
            {/* Real Snowflake brand logo — Iconify CDN (MIT-licensed) */}
            <div className="inline-flex relative mb-6">
              {/* v34.59 — icon tile + accent flipped from cyan → emerald to match brand */}
              <div className="relative w-20 h-20 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shadow-[0_8px_40px_-12px_rgba(16,185,129,0.5)]">
                <img
                  src="https://api.iconify.design/logos:snowflake-icon.svg"
                  alt="Snowflake"
                  width="44"
                  height="44"
                  loading="lazy"
                  className="w-11 h-11"
                />
              </div>
              <div className="absolute inset-0 rounded-2xl bg-emerald-500/30 blur-2xl opacity-50 -z-10" />
            </div>

            <p className="text-[11px] font-bold tracking-[0.25em] text-emerald-300 mb-3 inline-flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              FREE · NO SIGNUP · 60 SECONDS
            </p>
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 tracking-tight leading-[1.05]">
              The Snowflake Cost{' '}
              {/* v34.59 — was bg-clip-text gradient ending in emerald-400 which faded
                  the last letters of "Health Score" to look like glare. Solid bright
                  emerald + drop-shadow glow that stays fully readable. */}
              <span className="text-emerald-300 drop-shadow-[0_0_24px_rgba(16,185,129,0.45)]">Health Score</span>
            </h1>
            <p className="text-lg text-slate-300 max-w-xl mx-auto">
              10 questions. We'll show you your score, estimated monthly waste, and the top 3 fixes — instantly.
            </p>

            {/* Sample-output teaser — gives visitors a peek at what they get */}
            <div className="mt-7 max-w-md mx-auto rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur p-3 flex items-center gap-3">
              <div className="shrink-0 w-12 h-12 rounded-full bg-emerald-500/15 border-2 border-emerald-400/40 flex items-center justify-center">
                <span className="text-base font-extrabold text-emerald-300 tabular-nums">B+</span>
              </div>
              <div className="flex-1 text-left">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">sample score · 87/100</div>
                <div className="text-sm text-white font-semibold">≈ $4,200/mo waste · 3 fixes ready</div>
              </div>
              <span className="hidden sm:inline-flex text-[10px] font-mono text-cyan-300 tabular-nums">↓ that's you in 60s</span>
            </div>
          </div>
        )}

        <div className="rounded-2xl bg-white/[0.025] border border-white/10 p-6 sm:p-8 backdrop-blur-sm shadow-xl shadow-black/10" data-testid={`hs-question-${q.key}`}>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 leading-snug">{q.title}</h2>
          {q.sub && <p className="text-sm text-slate-400 mb-6">{q.sub}</p>}

          <div className="space-y-2">
            {q.options.map((opt) => {
              const selected = answers[q.key] === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => onPick(q.key, opt.value)}
                  className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all flex items-center justify-between group ${
                    selected
                      ? 'bg-gradient-to-r from-cyan-500/15 via-emerald-500/15 to-emerald-500/5 border-cyan-400/50 text-white shadow-[0_0_20px_-6px_rgba(34,211,238,0.4)]'
                      : 'bg-white/[0.02] border-white/10 text-slate-200 hover:bg-white/[0.05] hover:border-cyan-400/30 hover:translate-x-0.5'
                  }`}
                  data-testid={`hs-option-${q.key}-${opt.value}`}
                >
                  <span className="text-sm sm:text-base font-medium">{opt.label}</span>
                  <ArrowRight className={`w-4 h-4 transition-all ${selected ? 'text-cyan-300 translate-x-1' : 'text-slate-500 group-hover:text-cyan-300 group-hover:translate-x-0.5'}`} />
                </button>
              );
            })}
          </div>

          {step > 0 && (
            <button
              onClick={onBack}
              className="mt-6 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors"
              data-testid="hs-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
        </div>

        {/* Trust footer (only on first question) — upgraded from plain icons to
            data-rich tiles with cyan/emerald/amber accent rhythm */}
        {step === 0 && (
          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl bg-white/[0.02] p-4 border border-cyan-500/20 hover:border-cyan-400/40 transition-colors">
              <ShieldCheck className="w-5 h-5 text-cyan-300 mx-auto mb-1.5" />
              <p className="text-xs text-white font-semibold">Zero credentials</p>
              <p className="text-[10px] text-slate-500 mt-0.5">read-only · anonymous</p>
            </div>
            <div className="rounded-xl bg-white/[0.02] p-4 border border-emerald-500/20 hover:border-emerald-400/40 transition-colors">
              <Clock className="w-5 h-5 text-emerald-300 mx-auto mb-1.5" />
              <p className="text-xs text-white font-semibold">~60 seconds</p>
              <p className="text-[10px] text-slate-500 mt-0.5">10 quick taps</p>
            </div>
            <div className="rounded-xl bg-white/[0.02] p-4 border border-amber-500/20 hover:border-amber-400/40 transition-colors">
              <AlertTriangle className="w-5 h-5 text-amber-300 mx-auto mb-1.5" />
              <p className="text-xs text-white font-semibold">Real benchmarks</p>
              <p className="text-[10px] text-slate-500 mt-0.5">vs anon teams</p>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-white/5 px-4 py-8 mt-10 text-center text-xs text-slate-400 relative z-10">
        Built with care by <Link to="/about" className="text-emerald-400">CARTIEAI</Link> ·
        <Link to="/privacy-policy" className="text-slate-400 hover:text-white ml-2">Privacy</Link>
      </footer>
    </div>
  );
};

export default SnowflakeHealthScore;
